import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Label, Error, Hint } from '@progress/kendo-react-labels';
import { DatePicker, DatePickerProps } from '@progress/kendo-react-dateinputs';

interface DatePickerStateWrapperProps<T extends FieldValues>
  extends
    UseControllerProps<T>,
    Omit<DatePickerProps, 'defaultValue' | 'name'> {
  label?: string;
  disabled?: boolean;
  hint?: string;
  type?: string;
  placeholder?: string;
  optional?: boolean;
  wrapperClassName?: string;
  required?: boolean;
}

export const DatePickerStateWrapper = <T extends FieldValues>({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  disabled,
  hint,
  optional,
  label,
  wrapperClassName,
  required,
  ...rest
}: DatePickerStateWrapperProps<T>) => {
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({
    name,
    rules,
    shouldUnregister,
    defaultValue,
    control,
    disabled,
  });

  const id = field.name;
  const showRequired = required ?? Boolean(rules?.required);
  const showValidationMessage = Boolean(isTouched && error?.message);
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : '';
  const errorId = showValidationMessage ? `${id}_error` : '';

  return (
    <FieldWrapper className={`self-start ${wrapperClassName || ''}`}>
      <Label
        editorId={id}
        editorValid={!showValidationMessage}
        editorDisabled={disabled}
        optional={optional}
        className="k-form-label"
      >
        {label} {showRequired ? <span className="text-sm">*</span> : ''}
      </Label>
      <div className={'k-form-field-wrap'}>
        <DatePicker format={'dd.MM.yyyy'} {...rest} {...field} id={id} />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{error?.message}</Error>}
      </div>
    </FieldWrapper>
  );
};
